import { AfterViewInit, Component, input, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';
import { UserNotificationService } from 'src/app/modules/shared/user-notification.service';
import { ThemeService } from '../../services/theme.service';
import { predefinedThemes } from '../experience-configs';
import { SupportedCreativeExperience } from '../experiences';
import { ExperienceTheme, Theme, ThemeSpec } from '../models';
import { IUnitEditorBase } from '../unit-editor-base';

@Component({
  selector: 'app-unit-theme',
  templateUrl: './unit-theme.component.html',
  styleUrls: ['./unit-theme.component.scss'],
  standalone: false
})
export class UnitThemeComponent implements IUnitEditorBase<ExperienceTheme>, OnInit, AfterViewInit, OnDestroy {
  private themeService = inject(ThemeService);
  private notifications = inject(UserNotificationService);

  @Input() unit: ExperienceTheme;
  @Input() selectedLayout: SupportedCreativeExperience;
  isShowButton = input<boolean>(true)
  @ViewChild('themeForm') themeForm: NgForm;

  themes: ThemeSpec[];
  advancedBg: boolean;
  protected isChangedPrivate = false;
  private themeChangedSub: Subscription;
  private valueChangeSub: Subscription;

  constructor() {
    this.themes = this.themeService.themes;
  }

  get isOnlyImage(): boolean {
    return this.selectedLayout?.layoutId !== 103
  }

  get isClassicSlider(): boolean {
    return this.selectedLayout?.layoutId !== 130
  }

  get isRecommendSlider(): boolean {
    return this.selectedLayout?.layoutId === 207
  }

  get isShowColor(): boolean {
    if (this.selectedLayout?.layoutId === 400) {
      return false
    }

    return (this.selectedLayout?.purpose === 'promote' && !this.isShowButton());
  }

  ngOnInit(): void {
    this.advancedBg = !this.isSolid(this.unit.backgroundColor);
    this.themeChangedSub = this.themeService.themeChanged$.subscribe(() => this.isChangedPrivate = false);
  }

  ngAfterViewInit(): void {
    this.valueChangeSub = this.themeForm.form.valueChanges.subscribe(() => this.isChangedPrivate = true);
  }

  ngOnDestroy(): void {
    this.themeChangedSub?.unsubscribe();
    this.valueChangeSub?.unsubscribe();
  }

  // ------------------------------------------
  // API methods

  changeTheme(theme: Theme, layout: SupportedCreativeExperience, simpleBgDefaultColor: string = null): void {
    this.themeService.changeTheme(this.unit, theme, layout);
    this.advancedBg = !this.isSolid(this.unit.backgroundColor);
    if (this.advancedBg && simpleBgDefaultColor) {
      this.unit.backgroundColor = simpleBgDefaultColor;
      this.advancedBg = false;
    }
  }

  get isChanged(): boolean { return this.isChangedPrivate; }

  // ------------------------------------------
  // Component methods

  onThemeChange(theme: Theme) {
    this.isChangedPrivate = true;
    this.changeTheme(theme, this.selectedLayout);
  }

  isCustomThemes(themeName: string) {
    return predefinedThemes.find(t => t.name === themeName) === undefined;
  }

  changeBgType(event: MatCheckboxChange) {
    const advancesBg = event.checked;
    if (!advancesBg) {
      this.onThemeChange(this.unit.theme);
      this.unit.backgroundColor = '#fff';
    }
  }

  saveTheme(name: string, silent = false) {
    name = name || `${this.selectedLayout.name}_${new Date().toISOString()}`;
    this.themeService.saveTheme(this.unit, name);
    if (!silent) { this.notifications.notifySuccess(`Theme "${name}" Saved`); }
  }

  private isSolid(bg: string): boolean {
    return !bg || bg.startsWith('rgb') || bg.startsWith('#');
  }
}
